import { Injectable, OnDestroy } from '@angular/core';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { asapScheduler, Observable, share, subscribeOn } from 'rxjs';
import { MapSymbolService } from '../../services/map-symbol.service';
import { EsriUtils } from '../../utils/esri.utils';

export interface BufferConfigGroupLike {
  distanceInMeters: number;
  color: string;
  opacity: number;
}

export const defaultBufferConfig: BufferConfigGroupLike = {
  distanceInMeters: 0,
  color: '#111',
  opacity: 0.5,
};

@Injectable()
export class BufferService implements OnDestroy {
  private bufferLayer: GraphicsLayer;

  constructor(private mapSymbolService: MapSymbolService) {
    this.bufferLayer = new GraphicsLayer();
  }

  ngOnDestroy(): void {
    this.bufferLayer.removeAll();

    EsriUtils.destroyLayersRecursive(this.bufferLayer);
  }

  setup(layersMixin: __esri.LayersMixin) {
    layersMixin.add(this.bufferLayer);
  }

  buffer(
    target: __esri.Geometry | __esri.Geometry[],
    config: Partial<BufferConfigGroupLike>
  ): Observable<any> {
    const geometryList = (Array.isArray(target) ? target : [target]).filter(
      (x) => !!x
    );

    const polygonSymbol = !geometryList.length
      ? null
      : this.mapSymbolService.createPolygonSymbol({
          fillColor: config.color,
          opacity: config.opacity,
        });

    return new Observable((subscriber) => {
      const addGraphicsRange = (
        layer: __esri.GraphicsLayer,
        list: __esri.Geometry[],
        symbol?: __esri.Symbol
      ) => {
        if (!list?.length) {
          return;
        }

        const graphicList = list.map((geometry) => {
          const polygon = EsriUtils.geodesicBuffer(
            geometry,
            config.distanceInMeters
          );
          return new Graphic({
            geometry: polygon,
            symbol,
          });
        });

        layer.addMany(graphicList);
        subscriber.add(() => {
          layer.removeMany(graphicList);
        });
      };

      addGraphicsRange(this.bufferLayer, geometryList, polygonSymbol);
    }).pipe(subscribeOn(asapScheduler), share());
  }
}
